import { HTTP_STATUS } from '@/constants/http.constants';
import { isApiError } from '@/services/http/api-error';
import { httpClient } from '@/services/http/http-client';
import type { Page } from '@/types/api.types';
import {
  type AccountWithPassword,
  accountWithPasswordSchema,
  type UserAccount,
  userAccountSchema,
  type UserStatus,
} from '@/types/schemas';
import { blobToDataUrl } from '@/utils/file';

import {
  type Member,
  type MemberInput,
  memberSchema,
  type MemberStatus,
} from '../schemas/member.schema';

/** The only place this feature talks to the network. */

export interface MemberFilter {
  page: number;
  pageSize: number;
  q?: string | undefined;
  status?: MemberStatus | undefined;
  /** `day`: on a day pass today; `regular`: everyone else. */
  pass?: 'day' | 'regular' | undefined;
}

export function listMembers(filter: MemberFilter, signal?: AbortSignal): Promise<Page<Member>> {
  return httpClient.getPage('/members', { schema: memberSchema, query: { ...filter }, signal });
}

export function getMember(id: number, signal?: AbortSignal): Promise<Member> {
  return httpClient.get(`/members/${String(id)}`, {
    schema: memberSchema,
    ...(signal ? { signal } : {}),
  });
}

export function createMember(input: MemberInput): Promise<Member> {
  return httpClient.post('/members', input, { schema: memberSchema });
}

/**
 * The photo itself, as the raw request body. Needs `members:write`; the API replaces any photo
 * the member already had and returns the updated profile.
 */
export function uploadMemberPhoto(id: number, file: File): Promise<Member> {
  return httpClient.post(`/members/${String(id)}/photo`, file, { schema: memberSchema });
}

/**
 * The member's photo, as a `data:` URL an `<img>` can show. Only called when `photoPath` says
 * there is one. The image needs `members:read`, so the browser cannot load it by URL on its own.
 */
export async function getMemberPhoto(id: number, signal?: AbortSignal): Promise<string> {
  const { blob } = await httpClient.getBlob(`/members/${String(id)}/photo`, { signal });
  return blobToDataUrl(blob);
}

export function deleteMemberPhoto(id: number): Promise<Member> {
  return httpClient.delete(`/members/${String(id)}/photo`, { schema: memberSchema });
}

export function updateMember(id: number, input: Partial<MemberInput>): Promise<Member> {
  return httpClient.patch(`/members/${String(id)}`, input, { schema: memberSchema });
}

// --- The member's app login ---------------------------------------------------------------

/** The member's login, or null when they have none yet (the API answers 404). */
export async function getMemberAccount(
  memberId: number,
  signal?: AbortSignal,
): Promise<UserAccount | null> {
  try {
    return await httpClient.get(`/members/${String(memberId)}/account`, {
      schema: userAccountSchema,
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (isApiError(error) && error.status === HTTP_STATUS.NOT_FOUND) return null;
    throw error;
  }
}

export interface MemberAccountInput {
  mobile?: string | null;
  email?: string | null;
  password?: string;
}

export function createMemberAccount(
  memberId: number,
  input: MemberAccountInput,
): Promise<AccountWithPassword> {
  return httpClient.post(`/members/${String(memberId)}/account`, input, {
    schema: accountWithPasswordSchema,
  });
}

export function updateMemberAccount(
  memberId: number,
  input: { status?: UserStatus; mobile?: string | null; email?: string | null },
): Promise<UserAccount> {
  return httpClient.patch(`/members/${String(memberId)}/account`, input, {
    schema: userAccountSchema,
  });
}

export function resetMemberPassword(memberId: number): Promise<AccountWithPassword> {
  return httpClient.post(
    `/members/${String(memberId)}/account/reset-password`,
    {},
    { schema: accountWithPasswordSchema },
  );
}
